import { validatePassword } from '@/config/password-config';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { useSsoLogin } from '@/hooks/useSsoLogin';
import {
  ActivationDetails,
  ActivationService,
} from '@/services/activation-service';
import { isEmbeddedContext } from '@/utils/auth-context';
import { getErrorMessage } from '@/utils/common';
import {
  clearActivationSsoError,
  consumeActivationSsoReturnPath,
  readActivationSsoError,
} from '@/utils/sso-session-guard';
import { Icon } from '@iconify/react';
import { Button, Input, Link } from '@nextui-org/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface Tenant {
  id: number;
  name: string;
  slug: string;
}

function getActivationErrorData(
  error?: unknown
): Record<string, unknown> | undefined {
  return (error as { response?: { data?: Record<string, unknown> } })?.response
    ?.data;
}

function getActivationErrorCode(error?: unknown): string | undefined {
  const code = getActivationErrorData(error)?.code;
  return typeof code === 'string' ? code : undefined;
}

function resolveInvitationIsSSO(
  details?: ActivationDetails,
  error?: unknown
): boolean | undefined {
  if (details?.auth_method === 'sso') return true;
  if (details?.auth_method === 'password') return false;

  const data = getActivationErrorData(error);
  if (!data) return undefined;

  if (typeof data.isSSO === 'boolean') return data.isSSO;
  if (data.auth_method === 'sso') return true;
  if (data.auth_method === 'password') return false;
  return undefined;
}

const SSO_AUTO_REDIRECT_DELAY_MS = 2000;
const SSO_ATTEMPT_KEY_PREFIX = 'activation-sso-attempted:';

function getSsoAttemptKey(
  token: string | undefined,
  org: string | null
): string | null {
  if (token) return `${SSO_ATTEMPT_KEY_PREFIX}${token}`;
  if (org) return `${SSO_ATTEMPT_KEY_PREFIX}org:${org}`;
  return null;
}

function hasAttemptedSso(key: string | null): boolean {
  if (!key) return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function markSsoAttempted(key: string | null): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    // Without storage we cannot tell later visits apart; the redirect still works.
  }
}

function ActivationShell({
  icon,
  spin = false,
  title,
  subtitle,
  children,
}: {
  icon: string;
  spin?: boolean;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex h-full w-full items-center justify-center pt-20">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-large">
        <div className="flex flex-col items-center pb-6">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Icon
              icon={icon}
              className={`text-2xl text-primary ${spin ? 'animate-spin' : ''}`}
            />
          </div>
          <p className="text-xl font-medium">{title}</p>
          {subtitle && (
            <p className="text-small text-default-500 text-center">
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

const ActivatePage = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { login } = useAuthQuery();
  const { loginWithActivationSso } = useSsoLogin();

  const [isVisible, setIsVisible] = useState(false);
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [isSsoSigningIn, setIsSsoSigningIn] = useState(false);
  const [isAutoRedirecting, setIsAutoRedirecting] = useState(false);
  const [usePasswordFallback, setUsePasswordFallback] = useState(false);
  const ssoInFlight = useRef(false);
  const [formData, setFormData] = useState({
    new_password: '',
    new_password_confirm: '',
  });

  /**
   * SSO invitations can be sent before the token work lands, as `?org=` on a
   * tokenless link. There is nothing to verify or expire in that case.
   */
  const queryOrg = searchParams.get('org');

  /**
   * The first visit sends SSO users straight to their identity provider. Once
   * an attempt has been made in this tab (Back from the IdP, an Auth0 error,
   * reopening the link) we wait for a click instead, so we never loop.
   */
  const ssoAttemptKey = getSsoAttemptKey(token, queryOrg);
  const [isFirstSsoVisit, setIsFirstSsoVisit] = useState(
    () => !hasAttemptedSso(ssoAttemptKey)
  );
  const [ssoError, setSsoError] = useState(readActivationSsoError);

  // Back on the invitation: the error is in state and the return path has
  // done its job (a new attempt stores it again).
  useEffect(() => {
    clearActivationSsoError();
    consumeActivationSsoReturnPath();
  }, []);

  const {
    data: details,
    isLoading: isVerifying,
    error: verifyError,
  } = useQuery<ActivationDetails>({
    queryKey: ['activation', token],
    queryFn: () => ActivationService.verifyToken(token!),
    enabled: Boolean(token),
    retry: false,
  });

  const resolved: ActivationDetails | undefined =
    details ??
    (!token && queryOrg
      ? { email: '', auth_method: 'sso', organization: queryOrg }
      : undefined);

  const isSso = resolved?.auth_method === 'sso';

  const startSsoSignIn = async () => {
    if (ssoInFlight.current) return;

    ssoInFlight.current = true;
    markSsoAttempted(ssoAttemptKey);
    setIsFirstSsoVisit(false);
    setSsoError(null);
    setIsSsoSigningIn(true);
    try {
      await loginWithActivationSso({
        organization: resolved?.organization,
        loginHint: resolved?.email,
      });
    } catch {
      ssoInFlight.current = false;
      setIsSsoSigningIn(false);
      setIsAutoRedirecting(false);
    }
  };

  // Teams needs a popup and iframes need top-level navigation; browsers block
  // both without a click, so auto-redirect only in a normal browser tab.
  const shouldAutoRedirect =
    isSso && !usePasswordFallback && isFirstSsoVisit && !isEmbeddedContext();

  const startSsoSignInRef = useRef(startSsoSignIn);
  startSsoSignInRef.current = startSsoSignIn;

  useEffect(() => {
    if (!shouldAutoRedirect) return;

    const timeoutId = window.setTimeout(() => {
      setIsAutoRedirecting(true);
      void startSsoSignInRef.current();
    }, SSO_AUTO_REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timeoutId);
  }, [shouldAutoRedirect]);

  // Back from the IdP can restore this page from the bfcache mid-redirect.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      ssoInFlight.current = false;
      setIsSsoSigningIn(false);
      setIsAutoRedirecting(false);
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const cancelSsoRedirect = () => {
    ssoInFlight.current = false;
    setIsSsoSigningIn(false);
    setIsAutoRedirecting(false);
    setIsFirstSsoVisit(false);
  };

  const choosePasswordInstead = () => {
    cancelSsoRedirect();
    setUsePasswordFallback(true);
  };

  const invitationIsSSO = resolveInvitationIsSSO(details, verifyError);

  const resendMutation = useMutation({
    mutationFn: () =>
      ActivationService.resendInvitation({
        token: token!,
        ...(invitationIsSSO !== undefined ? { isSSO: invitationIsSSO } : {}),
      }),
    onSuccess: () => {
      toast.success('A new invitation is on its way to your inbox.');
    },
    onError: (error: unknown) => {
      console.error('Invitation resend failed:', error);
      const detail = (
        error as { response?: { data?: { error?: string; detail?: string } } }
      )?.response?.data;

      toast.error(
        detail?.error ??
          detail?.detail ??
          getErrorMessage(error) ??
          'We could not send a new invitation. Please try again.'
      );
    },
  });

  // A used SSO token cannot set a password, so swap it for a password invitation.
  const passwordLinkMutation = useMutation({
    mutationFn: () =>
      ActivationService.resendInvitation({ token: token!, isSSO: false }),
    onSuccess: () => {
      toast.success('A password link is on its way to your inbox.');
    },
    onError: (error: unknown) => {
      console.error('Password link request failed:', error);
      const detail = (
        error as { response?: { data?: { error?: string; detail?: string } } }
      )?.response?.data;

      toast.error(
        detail?.error ??
          detail?.detail ??
          getErrorMessage(error) ??
          'We could not send a password link. Please try again.'
      );
    },
  });

  const setPasswordMutation = useMutation({
    mutationFn: async () => {
      await ActivationService.setPassword({
        token: token!,
        new_password: formData.new_password,
        new_password_confirm: formData.new_password_confirm,
      });

      // Sign in with the credentials just chosen rather than sending the user
      // to the login form to retype them.
      return login.mutateAsync({
        email: resolved!.email,
        password: formData.new_password,
      });
    },
    onSuccess: (data) => {
      const tenants = (data?.tenants ?? []) as Tenant[];

      if (tenants.length === 1) {
        navigate(`/${tenants[0].slug}/home`, { replace: true });
        return;
      }

      navigate('/login', {
        state: { showTenantSelector: tenants.length > 1 },
        replace: true,
      });
    },
    onError: (error: unknown) => {
      console.error('Activation failed:', error);
      const detail = (
        error as { response?: { data?: { error?: string; detail?: string } } }
      )?.response?.data;

      toast.error(
        detail?.error ??
          detail?.detail ??
          'We could not finish setting up your account. Please try again.'
      );
    },
  });

  const passwordCheck = validatePassword(formData.new_password);
  const isPasswordMatch =
    formData.new_password === formData.new_password_confirm;
  const isSubmitting = setPasswordMutation.isPending || login.isPending;
  const isDisabled =
    !passwordCheck.isValid ||
    !isPasswordMatch ||
    !formData.new_password_confirm ||
    isSubmitting;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isDisabled) {
      setPasswordMutation.mutate();
    }
  };

  const verifyErrorCode = getActivationErrorCode(verifyError);
  const isInvalidInvitation = !token && !queryOrg;
  const isInvalidVerify = Boolean(verifyError) && verifyErrorCode === 'invalid';
  const isExpiredOrUsed =
    Boolean(verifyError) &&
    (verifyErrorCode === 'expired' || verifyErrorCode === 'used');

  if (isInvalidInvitation || isInvalidVerify) {
    return (
      <ActivationShell
        icon="solar:link-broken-linear"
        title="This link is not valid"
        subtitle="Open the most recent invitation in your inbox, or sign in if your account is already set up."
      >
        <div className="flex items-center justify-center">
          <Link className="text-default-500" href="/login" size="sm">
            Go to sign in
          </Link>
        </div>
      </ActivationShell>
    );
  }

  if (isVerifying) {
    return (
      <ActivationShell
        icon="solar:refresh-linear"
        spin
        title="Checking your invitation..."
      />
    );
  }

  if (isExpiredOrUsed) {
    return (
      <ActivationShell
        icon="solar:clock-circle-linear"
        title="This invitation has expired"
        subtitle="Invitation links are valid for 15 days and expire once you use them. We can send you a fresh one."
      >
        <Button
          color="primary"
          onPress={() => resendMutation.mutate()}
          isLoading={resendMutation.isPending}
          isDisabled={resendMutation.isSuccess}
        >
          {resendMutation.isSuccess
            ? 'Invitation sent'
            : 'Send me a new invitation'}
        </Button>
        <div className="flex items-center justify-center">
          <Link className="text-default-500" href="/login" size="sm">
            Already set up? Sign in
          </Link>
        </div>
      </ActivationShell>
    );
  }

  if (verifyError) {
    return (
      <ActivationShell
        icon="solar:link-broken-linear"
        title="This link is not valid"
        subtitle="Open the most recent invitation in your inbox, or sign in if your account is already set up."
      >
        <div className="flex items-center justify-center">
          <Link className="text-default-500" href="/login" size="sm">
            Go to sign in
          </Link>
        </div>
      </ActivationShell>
    );
  }

  if (isSso && !usePasswordFallback) {
    const isUsedToken = Boolean(token && details?.used);

    const passwordInsteadButton = !token ? null : isUsedToken ? (
      <Button
        variant="light"
        onPress={() => {
          // Otherwise the pending auto-redirect leaves mid-request.
          cancelSsoRedirect();
          passwordLinkMutation.mutate();
        }}
        isLoading={passwordLinkMutation.isPending}
        isDisabled={passwordLinkMutation.isSuccess}
      >
        {passwordLinkMutation.isSuccess
          ? 'Password link sent'
          : 'Email me a password link'}
      </Button>
    ) : (
      <Button variant="light" onPress={choosePasswordInstead}>
        Set a password instead
      </Button>
    );

    if (shouldAutoRedirect || isAutoRedirecting) {
      return (
        <ActivationShell
          icon="solar:refresh-linear"
          spin
          title="Taking you to your organization sign-in…"
        >
          {passwordInsteadButton}
        </ActivationShell>
      );
    }

    const isRetry = hasAttemptedSso(ssoAttemptKey);

    return (
      <ActivationShell
        icon="solar:shield-user-linear"
        title="Welcome to Mentora LiFT"
        subtitle={
          isRetry
            ? `${ssoError ? `${ssoError} ` : 'Organization sign-in did not finish. '}${
                !token
                  ? 'Please try again.'
                  : isUsedToken
                    ? 'Try again, or we can email you a password link.'
                    : 'Try again, or set a password instead.'
              }`
            : !token
              ? 'Sign in with your organization. No password needed.'
              : isUsedToken
                ? 'Sign in with your organization. If that does not work, we can email you a password link.'
                : 'Sign in with your organization. If that does not work, you can set a password instead.'
        }
      >
        <Button
          color="primary"
          onPress={() => {
            void startSsoSignIn();
          }}
          isLoading={isSsoSigningIn}
        >
          {isSsoSigningIn
            ? 'Taking you to sign-in...'
            : isRetry
              ? 'Try organization sign-in again'
              : 'Continue with organization sign-in'}
        </Button>
        {passwordInsteadButton}
      </ActivationShell>
    );
  }

  return (
    <ActivationShell
      icon="solar:lock-password-linear"
      title="Welcome to Mentora LiFT"
      subtitle={
        resolved?.email
          ? `Choose a password for ${resolved.email} to finish setting up your account.`
          : 'Choose a password to finish setting up your account.'
      }
    >
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <div className="flex flex-col">
          <Input
            label="Password"
            name="new_password"
            placeholder="Choose a password"
            type={isVisible ? 'text' : 'password'}
            variant="bordered"
            value={formData.new_password}
            onChange={handleInputChange}
            isRequired
            classNames={{
              base: '-mb-[2px]',
              inputWrapper:
                'rounded-b-none data-[hover=true]:z-10 group-data-[focus-visible=true]:z-10',
            }}
            endContent={
              <button type="button" onClick={() => setIsVisible(!isVisible)}>
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon={
                    isVisible ? 'solar:eye-closed-linear' : 'solar:eye-bold'
                  }
                />
              </button>
            }
          />
          <Input
            label="Confirm password"
            name="new_password_confirm"
            placeholder="Type it once more"
            type={isConfirmVisible ? 'text' : 'password'}
            variant="bordered"
            value={formData.new_password_confirm}
            onChange={handleInputChange}
            isRequired
            classNames={{ inputWrapper: 'rounded-t-none' }}
            endContent={
              <button
                type="button"
                onClick={() => setIsConfirmVisible(!isConfirmVisible)}
              >
                <Icon
                  className="pointer-events-none text-2xl text-default-400"
                  icon={
                    isConfirmVisible
                      ? 'solar:eye-closed-linear'
                      : 'solar:eye-bold'
                  }
                />
              </button>
            }
          />
        </div>

        {formData.new_password && !passwordCheck.isValid && (
          <div className="rounded-lg bg-danger-50 p-3">
            {passwordCheck.errors.map((error) => (
              <p key={error} className="text-small text-danger-600">
                {error}
              </p>
            ))}
          </div>
        )}

        {formData.new_password_confirm && !isPasswordMatch && (
          <div className="rounded-lg bg-danger-50 p-3">
            <p className="text-small text-danger-600">Passwords do not match</p>
          </div>
        )}

        <Button
          color="primary"
          type="submit"
          isLoading={isSubmitting}
          isDisabled={isDisabled}
        >
            {isSubmitting
              ? 'Signing you in...'
              : 'Create password and log in'}
        </Button>
        {usePasswordFallback && (
          <Button
            variant="light"
            onPress={() => setUsePasswordFallback(false)}
          >
            Use organization sign-in
          </Button>
        )}
      </form>
    </ActivationShell>
  );
};

export default ActivatePage;
